import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

const emailSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(255),
  categorySlug: z.string().trim().toLowerCase().max(64).regex(/^[a-z0-9-]+$/).optional(),
});

export type Subscriber = { id: string; email: string; created_at: string; is_internal?: boolean };

export const subscribeNewsletter = createServerFn({ method: "POST" })
  .inputValidator((d) => emailSchema.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: existing } = await supabaseAdmin
      .from("newsletter_subscribers")
      .select("id")
      .eq("email", data.email)
      .maybeSingle();
    if (existing) {
      const { data: sentConfirmation } = await supabaseAdmin
        .from("email_send_log")
        .select("id")
        .eq("recipient_email", data.email)
        .eq("template_name", "newsletter-confirmation")
        .eq("status", "sent")
        .limit(1)
        .maybeSingle();

      if (!sentConfirmation) {
        try {
          const { enqueueAppEmail } = await import("@/lib/email/enqueue.server");
          const { pickStarterPack } = await import("@/lib/starter-pack");
          const pack = pickStarterPack();
          await enqueueAppEmail({
            templateName: "newsletter-confirmation",
            recipientEmail: data.email,
            templateData: { recipient: data.email, downloadUrl: pack.downloadUrl, volumeLabel: pack.volumeLabel },
            idempotencyKey: `newsletter-confirm-recovery-${data.email}`,
          });
        } catch (e) {
          console.warn("newsletter confirmation recovery enqueue failed", e);
        }
      }

      return { ok: true, duplicate: true as const };
    }
    const { error } = await supabaseAdmin
      .from("newsletter_subscribers")
      .insert({ email: data.email, category_slug: data.categorySlug ?? null });
    if (error) {
      if ((error as any).code === "23505") return { ok: true, duplicate: true as const };
      throw new Error(error.message);
    }
    // Fire-and-forget confirmation email. Never block signup on email errors.
    try {
      const { enqueueAppEmail } = await import("@/lib/email/enqueue.server");
      const { pickStarterPack } = await import("@/lib/starter-pack");
      const pack = pickStarterPack();
      await enqueueAppEmail({
        templateName: "newsletter-confirmation",
        recipientEmail: data.email,
        templateData: { recipient: data.email, downloadUrl: pack.downloadUrl, volumeLabel: pack.volumeLabel },
        idempotencyKey: `newsletter-confirm-${data.email}`,
      });
    } catch (e) {
      console.warn("newsletter confirmation enqueue failed", e);
    }
    return { ok: true, duplicate: false as const };
  });

async function ensureAdmin(supabase: any, userId: string) {
  const { data } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (!data) throw new Error("Forbidden: admin role required");
}

export const listSubscribers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data, error }, { count: internalCount }, { count: totalCount }] = await Promise.all([
      supabaseAdmin
        .from("newsletter_subscribers")
        .select("id,email,created_at,is_internal")
        .eq("is_internal", false)
        .order("created_at", { ascending: false }),
      supabaseAdmin
        .from("newsletter_subscribers")
        .select("id", { count: "exact", head: true })
        .eq("is_internal", true),
      supabaseAdmin
        .from("newsletter_subscribers")
        .select("id", { count: "exact", head: true }),
    ]);
    if (error) throw new Error(error.message);
    return { subscribers: (data ?? []) as Subscriber[], internalCount: internalCount ?? 0, totalCount: totalCount ?? 0 };
  });

const issueSchema = z.object({
  subject: z.string().trim().min(3).max(200),
  bodyText: z.string().trim().min(10).max(20000),
  preview: z.string().trim().max(200).optional(),
});

export const sendNewsletterIssue = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => issueSchema.parse(d))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { enqueueAppEmail } = await import("@/lib/email/enqueue.server");

    const { data: subs, error } = await supabaseAdmin
      .from("newsletter_subscribers")
      .select("email")
      .eq("is_internal", false);
    if (error) throw new Error(error.message);

    const idemBase = `issue-${Date.now().toString(36)}`;
    let queued = 0;
    let skipped = 0;
    let failed = 0;
    for (const s of subs ?? []) {
      try {
        const res = await enqueueAppEmail({
          templateName: "newsletter-issue",
          recipientEmail: s.email,
          templateData: { subject: data.subject, bodyText: data.bodyText, preview: data.preview },
          idempotencyKey: `${idemBase}-${s.email}`,
        });
        if (res.ok) queued++;
        else skipped++;
      } catch (e) {
        console.warn("newsletter issue enqueue failed", s.email, e);
        failed++;
      }
    }
    return { total: subs?.length ?? 0, queued, skipped, failed };
  });
